import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  setLoggedInSubscriptionCenter,
  ESC_PROFILE_KEY,
  setEncryptedUserId,
} from '../../../global/authentication/authentication-manager.js'
import Step0 from './Step0.jsx'
import Step0Modal from './Step0Modal.jsx'
import Step1FP from './Step1FP.jsx'
import Step1Investor from './Step1Investor.jsx'
import Submitted from './Submitted.jsx'
import LoadingIndicator from './LoadingIndicator.jsx'
import {
  SERVICE_LABEL_ADVISOR,
  SERVICE_LABEL_WHOLESALER,
  SERVICE_LABEL_INVESTOR,
} from '../Landing/Helper/constants.js'
import { setItem } from '../../../global/data/synch-storage.js'
import {
  getFirms,
  postCheckSubscription,
  postSubscriptionsRegistration,
  setContactKey,
  setStorageProfile,
} from '../Data/SubscriptionCenterDataSource.js'

function EmailSubscriptionCenterSignInForm(props) {
  const {
    emailId,
    wholeSalerID,
    handleSuccessLogin,
    modalSideContent,
    isModal,
    handleSuccessOptionChange,
    hideInvestorChoice,
  } = props

  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [attemptedRegistration, setAttemptedRegistration] = useState(false)
  const [step, setStep] = useState(emailId ? 2 : 1)
  const [selectedOption, setSelectedOption] = useState(SERVICE_LABEL_ADVISOR)
  const [formData, setFormData] = useState({
    firstName: null,
    lastName: null,
    emailId: null,
    token: emailId || null,
    wholeSalerID: wholeSalerID || null,
    role: wholeSalerID
      ? SERVICE_LABEL_WHOLESALER
      : emailId
      ? null
      : SERVICE_LABEL_ADVISOR,
    firmName: null,
    firmId: null,
    parentFirmName: null,
    parentFirmId: null,
  })

  const [formValidation, setFormValidation] = useState({
    firstName: {
      error: false,
      errorMessage: 'Please enter your first name',
    },
    lastName: {
      error: false,
      errorMessage: 'Please enter your last name',
    },
    firmName: {
      error: false,
      errorMessage: 'Please enter your firm name',
    },
    emailId: {
      error: false,
      errorMessage: '',
    },
  })

  const [firms, setFirms] = useState([])
  const [signInProcessing, setSignInProcessing] = useState(false)

  useEffect(() => {
    if (formData.token && !attemptedSubmit) {
      submitForm()
    } else {
      updateFirms()
    }
    return () => {}
  })

  const updateFirms = async () => {
    if (firms.length < 1) {
      const _firms = await getFirms()
      if (_firms) {
        setFirms(_firms.value.firms)
      }
    }
  }

  const handleFormValidation = () => {
    let _formValidation = { ...formValidation }

    if (step === 1) {
      if (!formData.firstName) {
        _formValidation.firstName.error = true
      }

      if (!formData.lastName) {
        _formValidation.lastName.error = true
      }

      _formValidation.firmName.error =
        selectedOption === SERVICE_LABEL_ADVISOR && !formData.firmName

      if (!formData.emailId || !formData.emailId.includes('@')) {
        _formValidation.emailId.error = true

        selectedOption === SERVICE_LABEL_ADVISOR
          ? (_formValidation.emailId.errorMessage =
              'Please enter a valid firm email address')
          : (_formValidation.emailId.errorMessage =
              'Please enter a valid email address')
      }
    }

    return _formValidation
  }

  const handleNextClick = (event) => {
    event.preventDefault()

    const _formValidation = handleFormValidation()

    const isError = Object.entries(_formValidation).find(
      ([, value]) => value.error
    )

    if (!isError) {
      setSignInProcessing(true)
      submitForm()
    } else {
      setFormValidation(_formValidation)
    }
  }

  const handleOptionChange = async (event) => {
    const _selectedOption = event.target.value

    await setSelectedOption(_selectedOption)
    resetFormData(_selectedOption)

    if (handleSuccessOptionChange) {
      handleSuccessOptionChange(_selectedOption)
    }
  }

  const resetFormData = (_selectedOption) => {
    let _formData = { ...formData }

    _formData.firstName = null
    _formData.lastName = null
    _formData.emailId = null
    _formData.token = null
    _formData.role = _selectedOption
    _formData.firmName = null
    _formData.firmId = null
    _formData.parentFirmName = null
    _formData.parentFirmId = null
    setFormData(_formData)

    let _formValidation = { ...formValidation }
    _formValidation.emailId.error = false
    setFormValidation(_formValidation)
  }

  const handleInputChange = (event) => {
    const textInputValue = event.target.value
    const textFieldName = event.target.dataset.fieldName

    // setState doesnt update children
    let _formData = { ...formData }
    _formData[textFieldName] = textInputValue

    let _formValidation = { ...formValidation }
    _formValidation[textFieldName].error = false

    setFormData(_formData)
    setFormValidation(_formValidation)
  }

  const handleFirmInputChange = (event) => {
    const firmName = event.target.value

    const firm = lookupFirmByName(event.target.value)

    let _formData = { ...formData }
    _formData.firmName = firmName

    let _formValidation = { ...formValidation }
    _formValidation.firmName.error = false

    if (firm) {
      _formData.firmId = firm.firmId
      _formData.parentFirmName = firm.parentFirmNm
      _formData.parentFirmId = firm.parentFirmId
    } else {
      _formData.firmId = null
      _formData.parentFirmName = null
      _formData.parentFirmId = null
    }

    setFormData(_formData)
  }

  /**
   * Find the matching firm in the firms list
   */
  const lookupFirmByName = (name) => {
    let foundFirm = null

    firms.forEach((firm) => {
      if (firm.firmNm === name) {
        foundFirm = firm
      }
    })

    return foundFirm
  }

  const loginUser = (payload = {}) => {
    setStep(2)
    setItem(ESC_PROFILE_KEY, JSON.stringify({ ...formData, ...payload }))
    setStorageProfile(JSON.stringify({ ...formData, ...payload }))
    setEncryptedUserId(formData.emailId)
    setLoggedInSubscriptionCenter()
    handleSuccessLogin()
  }

  const checkRegisterNewUser = async () => {
    setAttemptedRegistration(true)

    await postSubscriptionsRegistration(formData).then((data) => {
      if (data.value) {
        localStorage.setItem('cti-pending-verification-modal', 'yes')
        if (window._satellite) {
          window._satellite.track('subscriptionCenterLogin', {
            user: 'New User',
          })
        }
        if (data.value.contactId) {
          setContactKey(data.value.contactId)
        }
        loginUser(data?.value)
      } else if (data.error) {
        let _formValidation = { ...formValidation }

        _formValidation.emailId.error = true

        switch (data.error.details.code) {
          // BLACKLISTED ERROR HANDLING
          case 'CTI_EMAILSUB_REG_1002':
            _formValidation.emailId.errorMessage =
              'Your email address domain must be firm-based. Please enter a new email address (ex: "name@firm.com").'
            setAttemptedRegistration(false)

            break
          default:
            _formValidation.emailId.errorMessage =
              'Unable to process your request at this time. Please try again later'
        }

        setFormValidation(_formValidation)
        setSignInProcessing(false)
        setStep(1)
      }
    })
  }

  const submitForm = async () => {
    setAttemptedSubmit(true)

    await postCheckSubscription(formData).then((data) => {
      if (data.value) {
        if (data.value.contactID) {
          setContactKey(data.value.contactID)
        }
        if (
          formData.role === SERVICE_LABEL_INVESTOR &&
          data.value.role !== SERVICE_LABEL_INVESTOR
        ) {
          let _formValidation = { ...formValidation }

          _formValidation.emailId.error = true
          _formValidation.emailId.errorMessage =
            'Please enter a different email address. You have already used this email address for a financial professional subscription.'

          setFormValidation(_formValidation)
          setSignInProcessing(false)
          setStep(1)
        } else if (
          formData.role === SERVICE_LABEL_ADVISOR &&
          data.value.role === SERVICE_LABEL_INVESTOR
        ) {
          let _formValidation = { ...formValidation }

          _formValidation.emailId.error = true
          _formValidation.emailId.errorMessage =
            'Please enter a different email address. You have already used this email address for a individual investor subscription.'

          setFormValidation(_formValidation)
          setSignInProcessing(false)
          setStep(1)
        } else if (data.value.contactStatusCd === 'Disqualified_By_Timeout') {
          let _formValidation = { ...formValidation }

          _formValidation.emailId.error = true
          _formValidation.emailId.errorMessage =
            'Unfortunately, your guest access has expired because we were unable to validate your registration information. Please call a Columbia Threadneedle representative at 800.248.2828 for assistance.'

          setFormValidation(_formValidation)
          setSignInProcessing(false)
          setStep(1)
        } else if (data.value.contactStatusCd === 'Disqualified') {
          let _formValidation = { ...formValidation }

          _formValidation.emailId.error = true
          _formValidation.emailId.errorMessage = `Unfortunately, your guest access has expired because we've determined you are not eligible. Please call a Columbia Threadneedle representative at 800.248.2828 for assistance.`

          setFormValidation(_formValidation)
          setSignInProcessing(false)
          setStep(1)
        } else {
          if (formData.token) {
            formData.firstName = data.value.firstName
            formData.lastName = data.value.lastName
            formData.emailId = data.value.emailId

            if (!formData.role) {
              formData.role = data.value.role
            }

            setFormData(formData)
          }

          if (window._satellite) {
            window._satellite.track('subscriptionCenterLogin', {
              user: 'Returning User',
            })
          }
          loginUser(data?.value)
        }
      } else if (data.error) {
        // TODO: determine all error codes and handle here
        let _formValidation = { ...formValidation }

        const errorCode = data.error.details && data.error.details.code

        switch (errorCode) {
          case 'CTI_CHECKSUBSCRIPTION_1001':
            if (!attemptedRegistration) {
              checkRegisterNewUser()
            }

            break
          default:
            _formValidation.emailId.errorMessage =
              'Unable to process your request at this time. Please try again later'

            setFormValidation(_formValidation)
            setSignInProcessing(false)
            setStep(1)
        }
      }
    })
  }

  const getSubmitButton = () => {
    return (
      <button
        href="#"
        onClick={handleNextClick}
        className="Button Button--primary u-paddingHoriz10gu u-marginBottom6gu u-textNoTransform"
        data-tracking="Button primary"
        disabled={signInProcessing}
        data-analytics="cesc-login"
      >
        Continue
        {signInProcessing && <LoadingIndicator />}
      </button>
    )
  }

  const getModalSubmitButton = () => {
    return (
      <button
        href="#"
        onClick={handleNextClick}
        className="Button Button--primary u-textUppercase u-bgColorBrandDark u-textColorWhite u-paddingHoriz10gu u-paddingVert2gu u-sm-marginBottom3gu"
        data-analytics="cesc-login"
        disabled={signInProcessing}
      >
        Subscribe
        {signInProcessing && <LoadingIndicator />}
      </button>
    )
  }

  const getStaticForm = () => {
    return step < 2 ? (
      <form>
        <div className="u-size6of12 u-sm-sizeFull u-paddingTop2gu u-bgColorLightest">
          <Step0
            handleOptionChange={handleOptionChange}
            selectedOption={selectedOption}
          />
          <div className="u-marginTop2gu">
            {selectedOption === SERVICE_LABEL_ADVISOR ? (
              <Step1FP
                handleInputChange={handleInputChange}
                handleFirmInputChange={handleFirmInputChange}
                formValidation={formValidation}
                firms={firms}
                nextButton={getSubmitButton}
              />
            ) : (
              <Step1Investor
                handleInputChange={handleInputChange}
                formValidation={formValidation}
                nextButton={getSubmitButton}
              />
            )}
          </div>
        </div>
      </form>
    ) : (
      <div className="Grid u-borderTop0 u-marginBottom6gu u-paddingBottom6gu">
        <Submitted name={formData.firstName} email={formData.emailId} />
      </div>
    )
  }

  const getModalForm = () => {
    return (
      <>
        <h6 className="u-textSizeMsr u-inlineBlock u-sm-block u-sm-marginBottom2gu u-marginRight2gu u-textSecondary">
          I am a(n):
        </h6>

        <Step0Modal
          handleOptionChange={handleOptionChange}
          selectedOption={selectedOption}
          hideInvestorChoice={hideInvestorChoice}
        />

        <div className="Grid u-marginTop4gu">
          <div
            className="u-lg-size6of12 u-sm-sizeFull u-bgColorLightest u-padding4gu"
            data-analytics="cesc-modal-step1"
          >
            <h3 className="u-marginBottom2gu">Step 1</h3>
            <p className="u-marginBottom4gu">
              Where should we send these emails?
            </p>
            {/* <EmailSubscriptionCenterSignInForm /> */}

            {selectedOption === SERVICE_LABEL_ADVISOR ? (
              <Step1FP
                selectedOption={selectedOption}
                handleInputChange={handleInputChange}
                handleFirmInputChange={handleFirmInputChange}
                formValidation={formValidation}
                firms={firms}
                signInProcessing={signInProcessing}
                nextButton={null}
                compact={true}
              />
            ) : (
              <Step1Investor
                selectedOption={selectedOption}
                handleInputChange={handleInputChange}
                formValidation={formValidation}
                signInProcessing={signInProcessing}
                nextButton={null}
                compact={true}
              />
            )}
          </div>
          {modalSideContent && modalSideContent}
          <hr
            className="Rule Rule--thin u-bgColorNeutral u-marginVert5gu u-sm-marginTop0"
            role="presentation"
          ></hr>
          <div className="u-flex u-sm-flexCol">{getModalSubmitButton()}</div>
        </div>
      </>
    )
  }

  return isModal ? getModalForm() : getStaticForm()
}

EmailSubscriptionCenterSignInForm.propTypes = {
  emailId: PropTypes.string,
  wholeSalerID: PropTypes.string,
  handleSuccessLogin: PropTypes.func,
  modalSideContent: PropTypes.object,
  isModal: PropTypes.bool,
  handleSuccessOptionChange: PropTypes.func,
  hideInvestorChoice: PropTypes.bool,
}

export default EmailSubscriptionCenterSignInForm
